import PropTypes from "prop-types";
import React from "react";
import param from "jquery-param";
import { Link, FlexTableSortHeaderCell, ResponsiveFlexTableWrapper } from "@evertrue/et-components";
import numeral from "numeral";
import { currencyFormatter } from "../utils/utils";
import RespondTo from "./respond-to";
import VolunteerStatsModal from "./volunteer-stats-modal";
import VolunteerContactCard from "./volunteer-contact-card";

const CONTACT_CLASSNAME = "volunteers-stats-table--contact-col";

const WIDTH = 300;

const round = num => numeral(num).format("0.[00]");

const PROP_TYPES = {
  items: PropTypes.array,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  total: PropTypes.number,
  pool: PropTypes.object,
  isVMSApp: PropTypes.bool,
  orgFiscalYear: PropTypes.string,
  handlePageChange: PropTypes.func,
  sortOrder: PropTypes.bool,
  interval: PropTypes.number,
  sortBy: PropTypes.string,
  handleSort: PropTypes.func,
  page: PropTypes.number,
  tableFlexBasis: PropTypes.any,
  isHeadAgentEnabled: PropTypes.bool,
  isMobile: PropTypes.bool,
  stagesForGroup: PropTypes.array,
  hasStagesFeature: PropTypes.bool,
  orgFiscalYearEnd: PropTypes.string,
  renderActivityCount: PropTypes.func,
  renderAssignmentCount: PropTypes.func,
};

const VolunteerStatsTable = ({
  items,
  limit,
  loading,
  total,
  pool,
  isVMSApp,
  orgFiscalYear,
  handlePageChange,
  sortOrder,
  interval,
  sortBy,
  handleSort,
  page,
  tableFlexBasis,
  isMobile,
  stagesForGroup,
  hasStagesFeature,
  orgFiscalYearEnd,
  isHeadAgentEnabled,
  renderActivityCount,
  renderAssignmentCount,
}) => {
  // this controls whether or the VolunteerStatsModal is open, we put the volData in state to pass it to the modal
  // this avoid having to render the modal in every row where the props could be passed
  const [volData, setVolData] = React.useState(undefined);

  return (
    <>
      <VolunteerStatsModal
        stagesForGroup={stagesForGroup}
        hasStagesFeature={hasStagesFeature}
        pool={pool}
        volunteerAnalyticsData={volData}
        onClose={() => setVolData(undefined)}
        orgFiscalYearEnd={orgFiscalYearEnd}
        isHeadAgentEnabled={isHeadAgentEnabled}
      />
      <RespondTo className="volunteers-stats-table--table">
        <ResponsiveFlexTableWrapper
          showResponsive={isMobile}
          emptyStateMessageText="No Volunteers"
          interval={interval}
          data={items}
          fetchMore={() => handlePageChange(page + 1)}
          caption={"Volunteer usage statistics"}
          getTrProps={rowData => ({
            onClick: () => setVolData(rowData),
          })}
          columns={[
            {
              key: 1,
              header: "Volunteer Info",
              accessor: i => i.solicitor_contact_id,
              width: WIDTH,
              cellClassName: CONTACT_CLASSNAME,
              headerClassName: CONTACT_CLASSNAME,
              sortBy: "name_last",
              Cell: ({ className, ...props }) => {
                return (
                  <td className={className} style={{ minWidth: WIDTH }}>
                    <VolunteerContactCard
                      isHeadAgentEnabled={isHeadAgentEnabled}
                      pool={pool}
                      orgFiscalYear={orgFiscalYear}
                      isVMSApp={isVMSApp}
                      {...props}
                    />
                  </td>
                );
              },
            },
            {
              key: 2,
              header: "Assigned",
              sortBy: "assignment_count",
              accessor: i => i.assignment_count,
              cellClassName: "volunteers-stats-table--number-stat",
              ...(!isMobile && {
                // on isMobile will render to the accessor
                Cell: cellParams => {
                  const formattedName = `${cellParams.name_first} ${cellParams.name_last}`;
                  const assignment_count = round(cellParams.assignment_count) || 0;
                  return (
                    <td className="et--flex-table--cell volunteers-stats-table--number-stat">
                      {!isVMSApp && assignment_count > 0 && typeof renderAssignmentCount === "function"
                        ? renderAssignmentCount(cellParams.solicitor_contact_id, formattedName, pool, assignment_count)
                        : assignment_count}
                    </td>
                  );
                },
              }),
            },
            {
              key: 3,
              header: "Participation",
              sortBy: "participation",
              accessor: i => `${round(i.participation) || 0}%`,
              cellClassName: "volunteers-stats-table--number-stat",
            },
            {
              key: 4,
              header: "Total Dollars",
              sortBy: "total_dollars_raised",
              accessor: i => currencyFormatter(i.total_dollars_raised) || 0,
              cellClassName: "volunteers-stats-table--number-stat",
            },
            {
              key: 5,
              header: "Activities Logged",
              sortBy: "total_activities",
              accessor: i => round(i.total_activities) || 0,
              cellClassName: "volunteers-stats-table--number-stat",
              ...(!isMobile && {
                // on isMobile will render to the accessor
                Cell: cellParams => {
                  const activity_count = round(cellParams.total_activities) || 0;
                  return (
                    <td className="et--flex-table--cell volunteers-stats-table--number-stat">
                      {!isVMSApp && activity_count > 0 && typeof renderActivityCount === "function"
                        ? renderActivityCount(cellParams.solicitor_contact_id, pool, activity_count)
                        : activity_count}
                    </td>
                  );
                },
              }),
            },
            {
              key: 6,
              header: "Assignments w/ Activities",
              sortBy: "activity_coverage",
              accessor: i => `${round(i.activity_coverage) || 0}%`,
              cellClassName: "volunteers-stats-table--number-stat",
            },
          ].map(col => ({
            ...col,
            header:
              !isMobile && items.length > 1 ? ( // disable sorting when 1 only row and on mobile
                <FlexTableSortHeaderCell
                  sortOrder={sortOrder}
                  isSorted={sortBy === col.sortBy}
                  onClick={() => handleSort(col.sortBy)}
                >
                  {col.header}
                </FlexTableSortHeaderCell>
              ) : (
                col.header
              ),
          }))}
          loading={loading}
          handlePageChange={handlePageChange}
          total={total}
          limit={limit}
          page={page}
          tableFlexBasis={tableFlexBasis}
        />
      </RespondTo>
    </>
  );
};

VolunteerStatsTable.propTypes = PROP_TYPES;

export default VolunteerStatsTable;
